<script setup>
import { ref, computed, inject, onMounted } from 'vue';
import { useShoppingCartStore } from '@/stores/shopping-cart.js';
import ProductQuantityInput from '@/components/form/ProductQuantityInput.vue';

const shoppingCartStore = useShoppingCartStore();
const { $translate } = inject('i18n');

const props = defineProps({
  productId: Number,
  showQuantityInput: {
    type: [Boolean, Number],
    default: false
  },
  buttonText: String,
  unit: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  lowestNumber: Number,
  packageSize: Number,
  isSticky: {
    type: Boolean,
    default: false
  },
  hasVariants: {
    type: Boolean,
    default: false
  },
  hasChilds: {
    type: Boolean,
    default: false
  }
});

const productId = ref(props.productId);
const quantity = ref(props.lowestNumber || 1);
const errMsg = ref('');

// styr tillfälligt variantval-låsning
const variantSelectionIncomplete = ref(false);

onMounted(() => {
  window.addEventListener('variant-selection-state', (e) => {
    variantSelectionIncomplete.value = !e.detail.allSelected;
  });
});

const localDisabled = computed(() => {
  if (props.disabled) return true; // lagersaldo
  if (props.hasVariants && variantSelectionIncomplete.value) return true; // val saknas
  return false;
});

const buyButtonText = computed(() => {
  if (props.disabled) return $translate('OUT_OF_STOCK');
  if (props.hasVariants && variantSelectionIncomplete.value) {
    return $translate('CHOOSE_VARIANT');
  }
  return props.buttonText || $translate('PRODUCT_BUTTON_BUY');
});

const buyProduct = () => {
  const product = {
    id: productId.value,
    quantity_diff: quantity.value,
    attributes: {}
  };

  const form = document.querySelector('form[name="add_product"]');
  if (form) {
    const formData = new FormData(form);
    for (const [key, value] of formData) {
      product.attributes[key] = value;
    }
  }

  shoppingCartStore.setProducts(product)
    .catch(error => {
      errMsg.value = error;
      setTimeout(() => { errMsg.value = null; }, 7000);
    });
};
</script>

<template>
  <div
    class="buy-button-wrapper"
    :data-sticky-buy-mobile="isSticky ? 'true' : 'false'"
  >
    <div v-if="showQuantityInput" class="input-wrapper">
      <ProductQuantityInput 
        v-model="quantity"
        :labelId="productId"
        :lowest-number="props.lowestNumber"
        :package-size="props.packageSize"
        :step="props.packageSize"
      />
      <label :for="productId">{{ props.unit }}</label>
    </div>

    <div class="button-wrapper">
      <button
        @click="buyProduct"
        type="button"
        class="button primary expanded"
        :class="{ 'full-width': !showQuantityInput }"
        :disabled="localDisabled"
      >
        {{ buyButtonText }}
      </button>
    </div>
  </div>

  <div v-show="errMsg" class="buy-button-error-wrapper">
    <span role="alert" v-html="errMsg" class="err"></span>
  </div>
</template>
